// src/services/geolocation/spatialIndex.js
// In-memory spatial index for client-side geo queries.
//
// Firestore geo queries with geohash ranges give us a superset of results
// within a bbox — we then client-side filter by exact haversine distance.
// For local queries (shelters near me, hospitals near me, gas stations near me)
// we don't need Firestore at all — this index handles them entirely in memory.
//
// Implementation: geohash-bucketed hash map.
// Each item is inserted under its geohash prefix at multiple precisions,
// allowing O(1) bucket lookup + O(k) distance filter where k << n.
//
// Suitable for up to ~10k items. Beyond that, use a proper R-tree.

import { encode, geohashQueryRanges, decodeBbox } from './geohash.js';
import { haversineKm } from './index.js';

export class SpatialIndex {
  // precision: the geohash precision used for bucketing (default 6 ≈ 1.2km cells)
  constructor(precision = 6) {
    this._precision = precision;
    this._buckets   = new Map();   // geohash → Item[]
    this._items     = new Map();   // id → Item (for O(1) delete/update)
  }

  // ── Insert ────────────────────────────────────────────────────
  // item must have: { id, lat, lon, ...any }
  insert(item) {
    if (item.lat == null || item.lon == null) return this;
    const hash = encode(item.lat, item.lon, this._precision);
    const enriched = { ...item, _geohash: hash };

    // Remove stale entry if id already exists
    this.delete(item.id);

    this._items.set(item.id, enriched);
    if (!this._buckets.has(hash)) this._buckets.set(hash, []);
    this._buckets.get(hash).push(enriched);

    return this;
  }

  insertAll(items) {
    for (const item of items) this.insert(item);
    return this;
  }

  // ── Delete ────────────────────────────────────────────────────
  delete(id) {
    const existing = this._items.get(id);
    if (!existing) return false;

    const bucket = this._buckets.get(existing._geohash);
    if (bucket) {
      const idx = bucket.findIndex((i) => i.id === id);
      if (idx !== -1) bucket.splice(idx, 1);
      if (bucket.length === 0) this._buckets.delete(existing._geohash);
    }
    this._items.delete(id);
    return true;
  }

  // ── Nearest neighbors ─────────────────────────────────────────
  // Returns up to `limit` items within `radiusKm`, sorted nearest-first.
  // Uses geohash bucket pre-filter to avoid scanning the full dataset.
  nearest(lat, lon, radiusKm, { limit = 20, filter } = {}) {
    const ranges    = geohashQueryRanges(lat, lon, radiusKm);
    const candidates = new Set();

    // Collect all items from matching buckets
    for (const [hash, items] of this._buckets) {
      for (const { start, end } of ranges) {
        if (hash >= start && hash <= end) {
          items.forEach((i) => candidates.add(i));
          break;
        }
      }
    }

    // Exact haversine filter + distance annotation
    const results = [];
    for (const item of candidates) {
      const km = haversineKm(lat, lon, item.lat, item.lon);
      if (km <= radiusKm) {
        if (!filter || filter(item)) {
          results.push({ ...item, _distanceKm: Math.round(km * 100) / 100 });
        }
      }
    }

    results.sort((a, b) => a._distanceKm - b._distanceKm);
    return results.slice(0, limit);
  }

  // ── Bounding-box query ────────────────────────────────────────
  // Returns all items within a lat/lon bounding box.
  withinBbox({ minLat, maxLat, minLon, maxLon }, { filter } = {}) {
    const results = [];
    for (const item of this._items.values()) {
      if (item.lat >= minLat && item.lat <= maxLat &&
          item.lon >= minLon && item.lon <= maxLon) {
        if (!filter || filter(item)) results.push(item);
      }
    }
    return results;
  }

  // ── Stats ─────────────────────────────────────────────────────
  get size()    { return this._items.size; }
  get buckets() { return this._buckets.size; }

  clear() {
    this._buckets.clear();
    this._items.clear();
  }
}

// ── App-level singleton indexes ───────────────────────────────────
// Populated by hooks when data loads; queried for nearest lookups.
// Using lazily-created singletons so unused indexes cost nothing.

let _shelterIndex   = null;
let _hospitalIndex  = null;
let _incidentIndex  = null;
let _gasIndex       = null;

export function getShelterIndex()  { return (_shelterIndex  ??= new SpatialIndex(7)); }
export function getHospitalIndex() { return (_hospitalIndex ??= new SpatialIndex(7)); }
export function getIncidentIndex() { return (_incidentIndex ??= new SpatialIndex(6)); }
export function getGasIndex()      { return (_gasIndex      ??= new SpatialIndex(7)); }

// Convenience: rebuild an index from a fresh data set
export function rebuildIndex(index, items) {
  index.clear();
  index.insertAll(items);
  return index;
}
